import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ArrowLeftOutlined, CheckOutlined, EnvironmentOutlined, MessageOutlined, NumberOutlined, } from "@ant-design/icons";
import { Alert, Button, Card, Flex, Modal, Skeleton, Space, Tag, Tooltip, Typography } from "antd";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
// Components
import RouteBuilder from "@/components/RouteBuilder/RouteBuilder";
import RouteTracking from "@/components/RouteTracking";
// Hooks
import { getRootStore } from "@/stores/RootStore";
// Styles
import RouteForm2 from "@/components/RouteForm2/RouteForm2";
import { RouteStatus } from "@/types/route/route.common";
import { canAccessChat } from "@/utils/permissions";
import styles from "./RoutePage.module.less";
import dayjs from "dayjs";
const { Title, Text } = Typography;
const RoutePage = observer(() => {
    const navigate = useNavigate();
    const location = useLocation();
    const { routeId } = useParams();
    const { singleRouteStore2: store, technicianLocationObserver, chatStore, authStore } = getRootStore();
    const { isLoading, routeBuilderStore, technicianSelectStore, serviceVehicleSelectStore, chargingStationSelectStore, } = store;
    const disabled = isLoading ||
        routeBuilderStore?.isLoading ||
        (store.route ? !store.canEditRoute(store.route.status) : false);
    const [startTime, setStartTime] = useState(null);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    // Load route data and handle tracking OR initialize from quick order
    useEffect(() => {
        const initPage = async () => {
            // Handle Quick Order - set start time BEFORE init so form gets correct initial value
            if (!routeId && location.state?.quickOrder) {
                const { baseId, orderId, plannedStartTime } = location.state.quickOrder;
                if (baseId && orderId && plannedStartTime) {
                    const start = dayjs(plannedStartTime);
                    // Set quick order start time in store before init
                    store.setQuickOrderStartTime(start);
                    setStartTime(start);
                }
            }
            await store.init(routeId);
            // Initialize route builder after store.init
            if (!routeId && location.state?.quickOrder && routeBuilderStore) {
                const { baseId, orderId, plannedStartTime, precalculatedPoints } = location.state.quickOrder;
                if (baseId && orderId && plannedStartTime) {
                    const start = dayjs(plannedStartTime);
                    if (precalculatedPoints && precalculatedPoints.length > 0) {
                        // Быстрый путь: данные уже рассчитаны — НЕТ вызова route-calculation
                        // Страница открывается мгновенно с готовыми таймингами и геометрией
                        await routeBuilderStore.initFromPrecalculated(baseId, orderId, start, precalculatedPoints);
                    }
                    else {
                        // Медленный путь (fallback): прямая навигация без dashboard или устаревший state
                        await routeBuilderStore.initFromQuickOrderByIds(baseId, orderId, start);
                    }
                }
            }
            else if (!routeId && !location.state?.quickOrder && routeBuilderStore) {
                // Direct creation path: initialize empty builder so user can add bases/orders
                await routeBuilderStore.initEmpty();
            }
            if (store.canTrackRoute) {
                handleTrackingOpen();
            }
        };
        initPage();
        return () => {
            store.clearRouteData();
            routeBuilderStore?.reset();
            handleTrackingClose();
        };
    }, [routeId, location.state]);
    const handleSave = async (values) => {
        await store.saveRoute(values);
        if (store.route?.id)
            navigate(`/routes/${store.route.id}`);
    };
    const handleSaveAndPlan = async (values) => {
        await store.saveAndPlanRoute(values);
        if (store.route?.id)
            navigate(`/routes/${store.route.id}`);
    };
    const handleTrackingOpen = async () => {
        const route = store.route;
        if (route) {
            try {
                if (technicianLocationObserver &&
                    !technicianLocationObserver.isActive) {
                    technicianLocationObserver
                        .startObserving(route.idTechnician)
                        .catch((error) => {
                        console.warn("Failed to auto-start technician location tracking:", error);
                    });
                }
            }
            catch (error) {
                console.warn("Error starting technician tracking:", error);
            }
        }
    };
    const handleTrackingClose = () => {
        // Stop technician tracking if active
        try {
            if (technicianLocationObserver?.isActive) {
                technicianLocationObserver.stopObserving();
            }
        }
        catch (error) {
            console.warn("Error stopping technician tracking during cleanup:", error);
        }
    };
    const handleSetPlanned = async () => {
        store.planRoute();
    };
    const handleFormValuesChange = (changed) => {
        if ("plannedStartTime" in changed && changed.plannedStartTime)
            setStartTime(changed.plannedStartTime);
    };
    const handleBack = () => {
        navigate("/routes");
    };
    // Задача 1: подтверждение отмены маршрута
    const handleCancelConfirm = async () => {
        await store.cancelRoute();
        setIsCancelModalOpen(false);
    };
    if (isLoading) {
        return _jsx(Skeleton, { active: true });
    }
    // Задача 2: номера заказов из route points (c orderNumber из JOIN)
    // Включаем все точки с заказами, в т.ч. отменённые (isDeleted=true)
    const orderNumbers = store.routePoints
        .filter((rp) => rp.idOrder && rp.orderNumber)
        .map((rp) => ({
        orderNumber: rp.orderNumber,
        orderId: rp.idOrder,
        isCancelled: rp.isDeleted === true ||
            rp.orderStatus === "CANCELLED" ||
            rp.orderStatus === "DISPATCHER_CANCELLED",
    }));
    return (_jsxs(Flex, { vertical: true, gap: 16, className: styles.container, children: [_jsxs("div", { className: styles.pageHeader, children: [_jsx(Button, { type: "text", icon: _jsx(ArrowLeftOutlined, {}), onClick: handleBack, className: styles.backButton, children: "\u041D\u0430\u0437\u0430\u0434 \u043A \u0441\u043F\u0438\u0441\u043A\u0443" }), orderNumbers.length > 0 && (_jsxs(Space, { size: 4, wrap: true, style: { marginTop: 4 }, children: [_jsxs(Text, { type: "secondary", children: [_jsx(NumberOutlined, {}), " \u0417\u0430\u043A\u0430\u0437\u044B:"] }), orderNumbers.map(({ orderNumber, orderId, isCancelled }) => (_jsx(Tag, { color: isCancelled ? "default" : "blue", style: {
                                    cursor: "pointer",
                                    textDecoration: isCancelled ? "line-through" : undefined,
                                    opacity: isCancelled ? 0.6 : 1,
                                }, onClick: () => navigate(`/orders/${orderId}`), children: orderNumber }, orderId)))] })), routeId && canAccessChat(authStore.userRole) && (() => {
                        const routeStatus = store.route?.status;
                        const CHAT_STATUSES = new Set([
                            RouteStatus.PLANNED,
                            RouteStatus.IN_PROGRESS,
                            RouteStatus.NOT_ACCEPTED,
                            RouteStatus.COMPLETED,
                        ]);
                        const isCreated = routeStatus === RouteStatus.CREATED;
                        const hasChatChannel = routeStatus ? CHAT_STATUSES.has(routeStatus) : false;
                        if (isCreated) {
                            return (_jsx(Tooltip, { title: "\u041A\u0430\u043D\u0430\u043B \u0431\u0443\u0434\u0435\u0442 \u0441\u043E\u0437\u0434\u0430\u043D \u043F\u0440\u0438 \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0435\u043D\u0438\u0438 \u043C\u0430\u0440\u0448\u0440\u0443\u0442\u0430", children: _jsx(Button, { type: "text", icon: _jsx(MessageOutlined, {}), disabled: true, className: styles.chatButton }) }));
                        }
                        if (hasChatChannel) {
                            return (_jsx(Button, { type: "text", icon: _jsx(MessageOutlined, {}), onClick: () => chatStore.openPanelWithRoute(routeId), className: styles.chatButton }));
                        }
                        return null;
                    })()] }), store.error && _jsx(Alert, { message: store.error, type: "error" }), _jsx(Card, { className: styles.formCard, children: _jsx(RouteForm2, { route: store.route ?? undefined, workShiftsLoader: (technicianId) => store.loadWorkShifts(technicianId), tableSelectStores: {
                        technicianSelectStore,
                        serviceVehicleSelectStore,
                        chargingStationSelectStore,
                    }, initialFormData: store.initialFormData, isLoading: isLoading, disabled: disabled, canCancel: store.canCancelRoute, onCancel: () => setIsCancelModalOpen(true), onValuesChange: handleFormValuesChange, onSave: handleSave, onSaveAndPlan: !routeId ? handleSaveAndPlan : undefined, expectedArrivalTime: routeBuilderStore?.expectedArrivalTime, extraButtons: store.canSetToPlanned && (_jsx(Button, { type: "default", icon: _jsx(CheckOutlined, {}), onClick: handleSetPlanned, loading: isLoading, children: "\u041C\u0430\u0440\u0448\u0440\u0443\u0442 \u0433\u043E\u0442\u043E\u0432 \u043A \u0432\u044B\u043F\u043E\u043B\u043D\u0435\u043D\u0438\u044E" })) }) }), startTime && (_jsxs(Card, { className: styles.routeBuilder, children: [_jsx(Title, { level: 4, children: "\u041F\u043E\u0441\u0442\u0440\u043E\u0435\u043D\u0438\u0435 \u043C\u0430\u0440\u0448\u0440\u0443\u0442\u0430" }), _jsx(RouteBuilder, { store: routeBuilderStore, startTime: startTime, routeId: routeId, disabled: disabled, onCancelOrder: (orderId) => store.cancelOrderInRoute(orderId) })] })), !!store.route && (_jsx(Card, { title: _jsxs(Space, { children: [_jsx(EnvironmentOutlined, {}), `Отслеживание маршрута ${store.route.id}`] }), className: styles.routeTracking, children: _jsx(RouteTracking, { routeData: store.route, routePoints: store.routePoints, width: "100%", height: "500px", showCard: false, showLoadingSpinner: true, showErrorAlert: true }) })), _jsx(Modal, { title: "\u041E\u0442\u043C\u0435\u043D\u0438\u0442\u044C \u043C\u0430\u0440\u0448\u0440\u0443\u0442?", open: isCancelModalOpen, onOk: handleCancelConfirm, onCancel: () => setIsCancelModalOpen(false), okText: "\u0414\u0430, \u043E\u0442\u043C\u0435\u043D\u0438\u0442\u044C", cancelText: "\u041D\u0435\u0442", okButtonProps: { danger: true, loading: isLoading }, children: _jsxs("p", { children: ["\u041C\u0430\u0440\u0448\u0440\u0443\u0442 \u0431\u0443\u0434\u0435\u0442 \u043F\u0435\u0440\u0435\u0432\u0435\u0434\u0451\u043D \u0432 \u0441\u0442\u0430\u0442\u0443\u0441 ", _jsx("strong", { children: "\u041E\u0442\u043C\u0435\u043D\u0451\u043D" }), ". \u0412\u0441\u0435 \u0442\u043E\u0447\u043A\u0438 \u043C\u0430\u0440\u0448\u0440\u0443\u0442\u0430 \u0431\u0443\u0434\u0443\u0442 \u0443\u0434\u0430\u043B\u0435\u043D\u044B. \u0417\u0430\u043A\u0430\u0437\u044B ", _jsx("strong", { children: "\u043E\u0441\u0442\u0430\u043D\u0443\u0442\u0441\u044F \u0431\u0435\u0437 \u0438\u0437\u043C\u0435\u043D\u0435\u043D\u0438\u0439" }), " \u0438 \u0431\u0443\u0434\u0443\u0442 \u0434\u043E\u0441\u0442\u0443\u043F\u043D\u044B \u0434\u043B\u044F \u043D\u0430\u0437\u043D\u0430\u0447\u0435\u043D\u0438\u044F \u0432 \u043D\u043E\u0432\u044B\u0435 \u043C\u0430\u0440\u0448\u0440\u0443\u0442\u044B."] }) })] }));
});
RoutePage.displayName = "RoutePage";
export default RoutePage;
